import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface Dinner {
  city: string;
  location: string;
  date: string;
  desc: string;
  link: string;
  image: string;
  about: string;
  hosts: string;
}
interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  dinner: Dinner | null;
}
export function EventModal({
  isOpen,
  onClose,
  dinner
}: EventModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    lifestyle: '',
    why: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);
  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setFormData({
          name: '',
          age: '',
          lifestyle: '',
          why: ''
        });
        setIsSubmitted(false);
      }, 300);
    }
  }, [isOpen]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    console.log('Registration submitted:', {
      event: dinner?.city,
      ...formData
    });
    setIsSubmitting(false);
    setIsSubmitted(true);
    setTimeout(() => {
      onClose();
    }, 2000);
  };
  if (!dinner) return null;
  return <AnimatePresence data-id="element-16">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-17" />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-18">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} className="relative w-full max-w-6xl bg-white rounded-2xl border-2 border-black shadow-2xl overflow-hidden" data-id="element-19">
              {/* Close Button */}
              <button onClick={onClose} className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white border-2 border-black hover:bg-gray-100 transition-colors shadow-lg" aria-label="Close modal" data-id="element-20">
                <X size={20} data-id="element-21" />
              </button>

              <div className="flex flex-col lg:flex-row max-h-[90vh]" data-id="element-22">
                {/* Left Side - Dinner Details */}
                <div className="lg:w-2/5 bg-gray-50 p-8 overflow-y-auto border-r-2 border-black" data-id="element-23">
                  <div className="space-y-6" data-id="element-24">
                    {/* Dinner Image */}
                    <div className="w-full h-64 rounded-xl overflow-hidden border-2 border-black" data-id="element-25">
                      <img src={dinner.image} alt={dinner.city} className="w-full h-full object-cover" data-id="element-26" />
                    </div>

                    {/* Title & Date */}
                    <div data-id="element-27">
                      <h2 className="text-2xl font-bold mb-2 font-editorial" data-id="element-28">
                        {dinner.city}
                      </h2>
                      <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-29">
                        <span data-id="element-30">{dinner.location}</span>
                        <span data-id="element-31">•</span>
                        <span className="font-bold" data-id="element-32">{dinner.date}</span>
                      </div>
                    </div>

                    {/* About This Dinner */}
                    <div data-id="element-33">
                      <h3 className="text-sm font-bold uppercase tracking-wider mb-2" data-id="element-34">
                        About This Dinner
                      </h3>
                      <p className="text-sm leading-relaxed text-gray-700" data-id="element-35">
                        {dinner.about}
                      </p>
                    </div>

                    {/* Hosts */}
                    <div data-id="element-36">
                      <h3 className="text-sm font-bold uppercase tracking-wider mb-2" data-id="element-37">
                        Your Hosts
                      </h3>
                      <p className="text-sm leading-relaxed text-gray-700" data-id="element-38">
                        {dinner.hosts}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right Side - Registration Form */}
                <div className="lg:w-3/5 p-12 overflow-y-auto" data-id="element-39">
                  {isSubmitted ? <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="flex items-center justify-center h-full" data-id="element-40">
                      <div className="text-center py-12" data-id="element-41">
                        <div className="w-16 h-16 bg-[#d4e20f] rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-black" data-id="element-42">
                          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-43">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-44" />
                          </svg>
                        </div>
                        <h3 className="text-2xl font-bold mb-2 font-editorial" data-id="element-45">
                          Application Received
                        </h3>
                        <p className="text-gray-600 italic" data-id="element-46">
                          We'll be in touch soon.
                        </p>
                      </div>
                    </motion.div> : <form onSubmit={handleSubmit} className="space-y-10 max-w-xl" data-id="element-47">
                      {/* Header */}
                      <div className="border-b border-gray-200 pb-6" data-id="element-48">
                        <h3 className="text-3xl font-editorial font-bold mb-3 leading-tight" data-id="element-49">
                          Application
                        </h3>
                        <p className="text-base text-gray-600 leading-relaxed italic" data-id="element-50">
                          We curate each gathering carefully. Share a bit about
                          yourself so we can create the right atmosphere.
                        </p>
                      </div>

                      {/* Name */}
                      <div data-id="element-51">
                        <label htmlFor="name" className="block text-sm uppercase tracking-widest mb-3 text-gray-500" data-id="element-52">
                          Your Name
                        </label>
                        <input type="text" id="name" required value={formData.name} onChange={e => setFormData({
                    ...formData,
                    name: e.target.value
                  })} className="w-full px-0 py-3 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-black transition-colors text-lg bg-transparent" placeholder="First and last name" data-id="element-53" />
                      </div>

                      {/* Age */}
                      <div data-id="element-54">
                        <label htmlFor="age" className="block text-sm uppercase tracking-widest mb-3 text-gray-500" data-id="element-55">
                          Age
                        </label>
                        <input type="number" id="age" required min="18" max="120" value={formData.age} onChange={e => setFormData({
                    ...formData,
                    age: e.target.value
                  })} className="w-full px-0 py-3 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-black transition-colors text-lg bg-transparent" placeholder="—" data-id="element-56" />
                      </div>

                      {/* Lifestyle */}
                      <div data-id="element-57">
                        <label htmlFor="lifestyle" className="block text-sm uppercase tracking-widest mb-3 text-gray-500" data-id="element-58">
                          How You Live
                        </label>
                        <select id="lifestyle" required value={formData.lifestyle} onChange={e => setFormData({
                    ...formData,
                    lifestyle: e.target.value
                  })} className="w-full px-0 py-3 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-black transition-colors text-lg bg-transparent appearance-none cursor-pointer" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23000' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0 center'
                  }} data-id="element-59">
                          <option value="" data-id="element-60">—</option>
                          <option value="travel-often" data-id="element-61">I travel often</option>
                          <option value="outside-6-months" data-id="element-62">
                            Outside my country 6+ months/year
                          </option>
                          <option value="settled" data-id="element-63">Mostly settled</option>
                          <option value="digital-nomad" data-id="element-64">Full-time nomad</option>
                        </select>
                      </div>

                      {/* Why this dinner */}
                      <div data-id="element-65">
                        <label htmlFor="why" className="block text-sm uppercase tracking-widest mb-3 text-gray-500" data-id="element-66">
                          What Draws You Here
                        </label>
                        <p className="text-sm text-gray-500 italic mb-4" data-id="element-67">
                          What about this gathering resonates with you? What are
                          you hoping to explore or discover?
                        </p>
                        <textarea id="why" required rows={6} value={formData.why} onChange={e => setFormData({
                    ...formData,
                    why: e.target.value
                  })} className="w-full px-0 py-3 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-black transition-colors text-base leading-relaxed resize-none bg-transparent" placeholder="Share your thoughts..." data-id="element-68" />
                      </div>

                      {/* Submit Button */}
                      <div className="pt-4" data-id="element-69">
                        <button type="submit" disabled={isSubmitting} className="w-full px-8 py-4 bg-[#d4e20f] border-2 border-black rounded-lg shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] font-bold text-sm uppercase tracking-widest hover:translate-y-1 hover:shadow-none active:translate-y-1 active:shadow-none transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-70">
                          {isSubmitting ? 'Sending...' : 'Submit Application'}
                        </button>
                      </div>
                    </form>}
                </div>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}